import React, { useEffect, useState, useMemo } from "react";
import axios from "axios";
import { useVipStore } from "../context/VipContext";
import { useNavigate } from "react-router-dom";
import { toast } from "react-toastify";

const api = axios.create({
  baseURL: process.env.REACT_APP_BASE_URL,
});

api.interceptors.request.use((config) => {
  const role = localStorage.getItem("role");
  const token = localStorage.getItem(`${role}Token`);
  if (token) config.headers.Authorization = `Bearer ${token}`;
  return config;
});

export default function User_VipDetailDuty() {
  const { selectedVip, handleBack } = useVipStore();
  const navigate = useNavigate();

  const [loading, setLoading] = useState(true);
  const [result, setResult] = useState(null);
  const [windowWidth, setWindowWidth] = useState(window.innerWidth);
  const isMobile = windowWidth <= 991;

  const storedVip = JSON.parse(localStorage.getItem("selectedVip"));
  const vip = selectedVip || storedVip;
  const vipId = vip?.id;

  useEffect(() => {
    const handleResize = () => setWindowWidth(window.innerWidth);
    window.addEventListener("resize", handleResize);
    return () => window.removeEventListener("resize", handleResize);
  }, []);

  useEffect(() => {
    if (!vipId) {
      toast.error("No VIP Selected!");
      navigate("/vip-management");
      return;
    }
    fetchAssignedGuards();
  }, [vipId]);

  const fetchAssignedGuards = async () => {
    try {
      const res = await api.get(`/api/assignments/${vipId}`);
      if (res.data?.officer?.length > 0) {
        setResult(res.data);
      } else {
        toast.info("No Officer assigned yet!");
      }
    } catch (err) {
      toast.error("Failed to fetch assigned officer!");
    } finally {
      setLoading(false);
    }
  };

  const summary = useMemo(() => {
    if (!result?.officer) return [];
    const map = {};
    result.officer.forEach((o) => {
      map[o.rank] = (map[o.rank] || 0) + 1;
    });
    return Object.entries(map).map(([level, assigned]) => ({
      level,
      assigned,
    }));
  }, [result]);

  if (loading) {
    return (
      <div className="loading-wrapper">
        <i className="fas fa-spinner fa-spin"></i>
        <p>Loading Assigned Officers...</p>
      </div>
    );
  }

  if (!result) {
    return (
      <div className="empty-state">
        <i className="fas fa-users-slash fa-3x"></i>
        <h3>No Officers Assigned</h3>
        <p>This VIP currently has no assigned security officers.</p>
        <button onClick={() => navigate("/viplist")} className="back-btn">
          <i className="fas fa-arrow-left"></i> Back to VIP List
        </button>
      </div>
    );
  }

  return (
    <div className="vipduty-wrapper">
      {/* Header */}
      <header className="vipduty-header">
        <h1>
          <i className="fas fa-user-tie"></i> Assigned Officers for {vip?.name}
        </h1>
        <p>Security detail and duty assignment overview</p>
      </header>

      {/* Main Content */}
      <main className="vipduty-main">
        {/* Rank Summary Card */}
        <div className="summary-card">
          <h3>
            <i className="fas fa-chart-pie"></i> Officer Rank Summary
          </h3>
          <div className="summary-grid">
            {summary.map((s, i) => (
              <div key={i} className="summary-item">
                <div className="rank-label">{s.level || "Unknown"}</div>
                <div className="rank-count">{s.assigned}</div>
                <div className="rank-text">Officer{s.assigned > 1 ? "s" : ""}</div>
              </div>
            ))}
          </div>
        </div>

        {/* Officers Details */}
        <div className="details-card">
          <h3>
            <i className="fas fa-shield-alt"></i> Detailed Officer List
          </h3>

          {/* Desktop Table */}
          {!isMobile ? (
            <div className="table-container">
              <table className="officers-table">
                <thead>
                  <tr>
                    <th>Officer ID</th>
                    <th>Name</th>
                    <th>Email</th>
                    <th>Rank</th>
                    <th>Experience</th>
                    <th>Status</th>
                    <th>Assigned From</th>
                    <th>Assigned Until</th>
                  </tr>
                </thead>
                <tbody>
                  {result.officer.map((o) => (
                    <tr key={o.id}>
                      <td>{o.id}</td>
                      <td>{o.name}</td>
                      <td>{o.email}</td>
                      <td>
                        <span className="rank-badge">{o.rank}</span>
                      </td>
                      <td>{o.experience} years</td>
                      <td>
                        <span
                          className={`status-badge ${
                            o.status === "Active" ? "active" : "inactive"
                          }`}
                        >
                          {o.status}
                        </span>
                      </td>
                      <td>{new Date(result.startAt).toLocaleString()}</td>
                      <td>{new Date(result.endAt).toLocaleString()}</td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          ) : (
            /* Mobile Cards */
            <div className="mobile-cards">
              {result.officer.map((o) => (
                <div key={o.id} className="mobile-officer-card">
                  <div className="card-header">
                    <h4>{o.name}</h4>
                    <span className="rank-badge">{o.rank}</span>
                  </div>
                  <div className="card-body">
                    <div className="info-row">
                      <span>ID</span>
                      <strong>{o.id}</strong>
                    </div>
                    <div className="info-row">
                      <span>Email</span>
                      <strong>{o.email}</strong>
                    </div>
                    <div className="info-row">
                      <span>Experience</span>
                      <strong>{o.experience} years</strong>
                    </div>
                    <div className="info-row">
                      <span>Status</span>
                      <strong
                        className={`status-badge ${
                          o.status === "Active" ? "active" : "inactive"
                        }`}
                      >
                        {o.status}
                      </strong>
                    </div>
                    <div className="info-row">
                      <span>Assigned From</span>
                      <strong>{new Date(result.startAt).toLocaleString()}</strong>
                    </div>
                    <div className="info-row">
                      <span>Assigned Until</span>
                      <strong>{new Date(result.endAt).toLocaleString()}</strong>
                    </div>
                  </div>
                </div>
              ))}
            </div>
          )}

          <div className="action-buttons">
            <button
              onClick={() => {
                handleBack();
                navigate("/viplist");
              }}
              className="back-btn"
            >
              <i className="fas fa-arrow-left"></i> Back to VIP List
            </button>
          </div>
        </div>
      </main>

      <style jsx>{`
        .vipduty-wrapper {
          min-height: 100vh;
          background: linear-gradient(135deg, #0f172a 0%, #1e293b 100%);
          color: #e2e8f0;
          padding: 2rem 1rem;
        }

        .vipduty-header {
          text-align: center;
          margin-bottom: 3rem;
        }

        .vipduty-header h1 {
          font-size: 2.4rem;
          font-weight: 700;
          color: #60a5fa;
          margin: 0 0 0.8rem;
          display: flex;
          align-items: center;
          justify-content: center;
          gap: 0.8rem;
        }

        .vipduty-header p {
          opacity: 0.85;
          font-size: 1.1rem;
        }

        .vipduty-main {
          max-width: 1400px;
          margin: 0 auto;
        }

        .summary-card,
        .details-card {
          background: rgba(30, 41, 59, 0.8);
          backdrop-filter: blur(12px);
          border-radius: 20px;
          padding: 2rem;
          margin-bottom: 2rem;
          border: 1px solid rgba(59, 130, 246, 0.3);
          box-shadow: 0 20px 40px rgba(0, 0, 0, 0.3);
          transition: all 0.3s ease;
        }

        .summary-card:hover,
        .details-card:hover {
          transform: translateY(-5px);
          box-shadow: 0 25px 50px rgba(59, 130, 246, 0.15);
        }

        .summary-card h3,
        .details-card h3 {
          font-size: 1.5rem;
          color: #60a5fa;
          margin-bottom: 1.5rem;
          display: flex;
          align-items: center;
          gap: 0.6rem;
        }

        .summary-grid {
          display: grid;
          grid-template-columns: repeat(auto-fit, minmax(200px, 1fr));
          gap: 1.5rem;
        }

        .summary-item {
          background: rgba(15, 23, 42, 0.8);
          padding: 1.5rem;
          border-radius: 16px;
          text-align: center;
          border: 1px solid rgba(59, 130, 246, 0.3);
        }

        .rank-label {
          font-size: 1.1rem;
          font-weight: 600;
          color: #60a5fa;
          margin-bottom: 0.5rem;
        }

        .rank-count {
          font-size: 2.2rem;
          font-weight: 700;
          color: white;
        }

        .rank-text {
          font-size: 0.95rem;
          opacity: 0.8;
        }

        .table-container {
          overflow-x: auto;
          border-radius: 16px;
          margin-bottom: 2rem;
        }

        .officers-table {
          width: 100%;
          border-collapse: separate;
          border-spacing: 0;
        }

        .officers-table th {
          background: rgba(15, 23, 42, 0.9);
          padding: 1.2rem 1rem;
          text-align: left;
          color: #60a5fa;
          font-weight: 600;
          font-size: 1rem;
          border-bottom: 2px solid #2563eb;
        }

        .officers-table td {
          padding: 1.2rem 1rem;
          border-bottom: 1px solid rgba(100, 116, 139, 0.3);
        }

        .officers-table tr:hover {
          background: rgba(59, 130, 246, 0.1);
        }

        .rank-badge {
          background: #2563eb;
          color: white;
          padding: 0.4rem 0.9rem;
          border-radius: 30px;
          font-weight: 600;
          font-size: 0.9rem;
        }

        .status-badge {
          padding: 0.5rem 1rem;
          border-radius: 30px;
          font-weight: 600;
          font-size: 0.9rem;
        }

        .status-badge.active {
          background: #10b98133;
          color: #10b981;
          border: 1px solid #10b981;
        }

        .status-badge.inactive {
          background: #ef444433;
          color: #ef4444;
          border: 1px solid #ef4444;
        }

        .mobile-cards {
          display: flex;
          flex-direction: column;
          gap: 1.5rem;
          margin-top: 1rem;
        }

        .mobile-officer-card {
          background: rgba(15, 23, 42, 0.8);
          border-radius: 16px;
          padding: 1.5rem;
          border-left: 5px solid #3b82f6;
        }

        .card-header {
          display: flex;
          justify-content: space-between;
          align-items: center;
          margin-bottom: 1rem;
          flex-wrap: wrap;
          gap: 0.5rem;
        }

        .card-header h4 {
          margin: 0;
          font-size: 1.3rem;
          color: white;
        }

        .card-body {
          display: grid;
          grid-template-columns: 1fr 1fr;
          gap: 0.8rem;
        }

        .info-row {
          display: flex;
          flex-direction: column;
        }

        .info-row span {
          font-size: 0.9rem;
          opacity: 0.8;
        }

        .info-row strong {
          font-size: 1rem;
          color: white;
          margin-top: 0.2rem;
        }

        .action-buttons {
          display: flex;
          justify-content: center;
          margin-top: 2rem;
        }

        .back-btn {
          background: linear-gradient(135deg, #ef4444, #dc2626);
          color: white;
          border: none;
          padding: 1rem 2rem;
          border-radius: 14px;
          font-weight: 600;
          font-size: 1.1rem;
          cursor: pointer;
          display: flex;
          align-items: center;
          gap: 0.6rem;
          transition: all 0.3s ease;
        }

        .back-btn:hover {
          transform: translateY(-3px);
          box-shadow: 0 10px 25px rgba(239, 68, 68, 0.3);
        }

        .loading-wrapper,
        .empty-state {
          min-height: 70vh;
          display: flex;
          flex-direction: column;
          align-items: center;
          justify-content: center;
          text-align: center;
          padding: 2rem;
        }

        .loading-wrapper i {
          font-size: 3rem;
          color: #60a5fa;
          margin-bottom: 1rem;
          animation: spin 1.5s linear infinite;
        }

        @keyframes spin {
          to { transform: rotate(360deg); }
        }

        .empty-state i {
          color: #f59e0b;
          margin-bottom: 1.5rem;
        }

        .empty-state h3 {
          font-size: 1.8rem;
          margin-bottom: 0.8rem;
        }

        .empty-state p {
          opacity: 0.8;
          margin-bottom: 2rem;
        }

        @media (max-width: 991px) {
          .card-body {
            grid-template-columns: 1fr;
          }
        }

        @media (max-width: 768px) {
          .vipduty-header h1 {
            font-size: 2rem;
            flex-direction: column;
          }

          .summary-grid {
            grid-template-columns: 1fr 1fr;
          }

          .summary-item {
            padding: 1.2rem;
          }

          .rank-count {
            font-size: 2rem;
          }
        }

        @media (max-width: 576px) {
          .vipduty-wrapper {
            padding: 1rem;
          }

          .summary-grid {
            grid-template-columns: 1fr;
          }

          .details-card,
          .summary-card {
            padding: 1.5rem;
          }
        }
      `}</style>
    </div>
  );
}

// import React, { useEffect, useState, useMemo } from "react";
// import axios from "axios";
// import { useVipStore } from "../context/VipContext";
// import { useNavigate } from "react-router-dom";
// import { toast } from "react-toastify";

// /* ------------------- AXIOS INSTANCE ------------------- */
// const api = axios.create({
//   baseURL: process.env.REACT_APP_BASE_URL,
// });

// api.interceptors.request.use((config) => {
//   const role = localStorage.getItem("role");
//   const token = localStorage.getItem(`${role}Token`);
//   if (token) config.headers.Authorization = `Bearer ${token}`;
//   return config;
// });

// export default function User_VipDetailDuty() {
//   const { selectedVip, handleBack } = useVipStore();
//   const navigate = useNavigate();

//   const [loading, setLoading] = useState(true);
//   const [result, setResult] = useState(null);
//   const [isMobile, setIsMobile] = useState(window.innerWidth < 768);

//   // ⭐ 1. READ VIP FROM LOCAL STORAGE (REFRESH SAFE)
//   const storedVip = JSON.parse(localStorage.getItem("selectedVip"));

//   // ⭐ 2. USE CONTEXT FIRST, FALLBACK TO STORAGE
//   const vip = selectedVip || storedVip;

//   // ⭐ 3. FINAL VIP ID
//   const vipId = vip?.id;

//   /* ---------------- RESPONSIVE ---------------- */
//   useEffect(() => {
//     const resize = () => setIsMobile(window.innerWidth < 768);
//     window.addEventListener("resize", resize);
//     return () => window.removeEventListener("resize", resize);
//   }, []);

//   /* ---------------- FETCH DATA ---------------- */
//   useEffect(() => {
//     if (!vipId) {
//       toast.error("No VIP Selected!");
//       navigate("/vip-management");
//       return;
//     }
//     fetchAssignedGuards();
//   }, [vipId]);

//   const fetchAssignedGuards = async () => {
//     try {
//       const res = await api.get(`/api/assignments/${vipId}`);
//       if (res.data?.officer?.length > 0) {
//         setResult(res.data);
//       } else {
//         toast.info("No Officer assigned yet!");
//       }
//     } catch (err) {
//       toast.error("Failed to fetch assigned officer!");
//     } finally {
//       setLoading(false);
//     }
//   };

//   /* ---------------- SUMMARY ---------------- */
//   const summary = useMemo(() => {
//     if (!result?.officer) return [];
//     const map = {};
//     result.officer.forEach((o) => {
//       map[o.rank] = (map[o.rank] || 0) + 1;
//     });
//     return Object.entries(map).map(([level, assigned]) => ({
//       level,
//       assigned,
//     }));
//   }, [result]);

//   if (loading)
//     return <h3 style={{ padding: 30 }}>Loading Assigned Officer...</h3>;
//   if (!result)
//     return <h3 style={{ padding: 30 }}>No Assigned Officer Found</h3>;

//   return (
//     <div style={styles.container}>
//       <h2 style={styles.title}>Assigned Officer for {vip?.name}</h2>

//       {/* ================= SUMMARY ================= */}
//       <div style={styles.card}>
//         <h3>Officer Summary</h3>

//         <div style={styles.tableWrapper}>
//           <table style={styles.table}>
//             <thead>
//               <tr>
//                 <th style={styles.tableHeader}>Rank</th>
//                 <th style={styles.tableHeader}>Assigned</th>
//               </tr>
//             </thead>
//             <tbody>
//               {summary.map((s, i) => (
//                 <tr key={i}>
//                   <td style={styles.tableCell}>{s.level}</td>
//                   <td style={styles.tableCell}>{s.assigned}</td>
//                 </tr>
//               ))}
//             </tbody>
//           </table>
//         </div>
//       </div>

//       {/* ================= DETAILS ================= */}
//       <div style={styles.responseBox}>
//         <h3>Officers Details</h3>

//         {/* ===== DESKTOP / TABLET ===== */}
//         {!isMobile ? (
//           <div style={styles.tableWrapper}>
//             <table style={styles.table}>
//               <thead>
//                 <tr>
//                   <th style={styles.tableHeader}>Officer ID</th>
//                   <th style={styles.tableHeader}>Name</th>
//                   <th style={styles.tableHeader}>Email</th>
//                   <th style={styles.tableHeader}>Rank</th>
//                   <th style={styles.tableHeader}>Experience</th>
//                   <th style={styles.tableHeader}>Status</th>
//                   <th style={styles.tableHeader}>Assigned At</th>
//                   <th style={styles.tableHeader}>Assigned Till</th>
//                 </tr>
//               </thead>
//               <tbody>
//                 {result.officer.map((o) => (
//                   <tr key={o.id}>
//                     <td style={styles.tableCell}>{o.id}</td>
//                     <td style={styles.tableCell}>{o.name}</td>
//                     <td style={styles.tableCell}>{o.email}</td>
//                     <td style={styles.tableCell}>{o.rank}</td>
//                     <td style={styles.tableCell}>{o.experience} yrs</td>
//                     <td style={styles.tableCell}>{o.status}</td>
//                     <td style={styles.tableCell}>
//                       {new Date(result.startAt).toLocaleString()}
//                     </td>
//                     <td style={styles.tableCell}>
//                       {new Date(result.endAt).toLocaleString()}
//                     </td>
//                   </tr>
//                 ))}
//               </tbody>
//             </table>
//           </div>
//         ) : (
//           /* ===== MOBILE CARDS ===== */
//           <div style={styles.mobileList}>
//             {result.officer.map((o) => (
//               <div key={o.id} style={styles.mobileCard}>
//                 <div style={styles.mobileRow}>
//                   <span>ID</span>
//                   <b>{o.id}</b>
//                 </div>
//                 <div style={styles.mobileRow}>
//                   <span>Name</span>
//                   <b>{o.name}</b>
//                 </div>
//                 <div style={styles.mobileRow}>
//                   <span>Email</span>
//                   <b>{o.email}</b>
//                 </div>
//                 <div style={styles.mobileRow}>
//                   <span>Rank</span>
//                   <b>{o.rank}</b>
//                 </div>
//                 <div style={styles.mobileRow}>
//                   <span>Experience</span>
//                   <b>{o.experience} yrs</b>
//                 </div>
//                 <div style={styles.mobileRow}>
//                   <span>Status</span>
//                   <b>{o.status}</b>
//                 </div>
//                 <div style={styles.mobileRow}>
//                   <span>Assigned</span>
//                   <b>{new Date(result.startAt).toLocaleString()}</b>
//                 </div>
//                 <div style={styles.mobileRow}>
//                   <span>Till</span>
//                   <b>{new Date(result.endAt).toLocaleString()}</b>
//                 </div>
//               </div>
//             ))}
//           </div>
//         )}

//         <button
//           style={{ ...styles.btn, marginTop: 20 }}
//           onClick={() => {
//             handleBack(); // ✅ CLEAR CONTEXT + STORAGE
//             navigate("/viplist"); // ✅ GO BACK
//           }}
//         >
//           ⬅ Back
//         </button>
//       </div>
//     </div>
//   );
// }

// /* ---------------- STYLES ---------------- */
// const styles = {
//   container: { padding: 30 },

//   title: {
//     fontSize: 28,
//     fontWeight: "bold",
//     marginBottom: 20,
//     color: "#1967d2",
//   },

//   card: {
//     background: "#fff",
//     padding: 20,
//     borderRadius: 12,
//     marginBottom: 20,
//   },

//   responseBox: {
//     background: "rgba(255,255,255,0.55)",
//     padding: 25,
//     borderRadius: 14,
//   },

//   btn: {
//     background: "#1967d2",
//     color: "#fff",
//     border: "none",
//     padding: "12px 20px",
//     borderRadius: 10,
//     cursor: "pointer",
//     fontSize: 16,
//   },

//   /* ===== TABLE RESPONSIVE ===== */
//   tableWrapper: {
//     width: "100%",
//     overflowX: "auto",
//     WebkitOverflowScrolling: "touch",
//   },

//   table: {
//     width: "100%",
//     minWidth: "900px",
//     marginTop: 15,
//     borderCollapse: "collapse",
//   },

//   tableHeader: {
//     background: "#1967d2",
//     color: "#fff",
//     padding: "10px 12px",
//     textAlign: "left",
//     whiteSpace: "nowrap",
//     position: "sticky",
//     top: 0,
//   },

//   tableCell: {
//     padding: "10px 12px",
//     borderBottom: "1px solid #ddd",
//     whiteSpace: "nowrap",
//     fontSize: 14,
//   },

//   /* ===== MOBILE ===== */
//   mobileList: {
//     display: "flex",
//     flexDirection: "column",
//     gap: 15,
//     marginTop: 15,
//   },

//   mobileCard: {
//     background: "#fff",
//     padding: 15,
//     borderRadius: 12,
//     borderLeft: "4px solid #1967d2",
//   },

//   mobileRow: {
//     display: "flex",
//     justifyContent: "space-between",
//     padding: "6px 0",
//     fontSize: 14,
//   },
// };
